import React, { useState, useRef, useMemo } from 'react';
import Papa from 'papaparse';

const DEFAULT_TC_HEADERS = [
  'Title', 'Priority', 'Type', 'Preconditions', 'Steps', 'Expected Result',
  'Locale', 'Brand', 'Area', 'Automation Status', 'Testcase Subtype',
  'Test Type', 'Module', 'Touch Point'
];

function TestCaseTable({ testCases, onUpdate, headers: headersProp }) {
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [hiddenColumns, setHiddenColumns] = useState(new Set());
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [columnWidths, setColumnWidths] = useState({});
  const fileInputRef = useRef(null);
  const columnPickerRef = useRef(null);

  const handleResizeStart = (e, header) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const th = e.target.parentElement;
    const startWidth = th.offsetWidth;
    const onMouseMove = (moveEvent) => {
      const newWidth = Math.max(60, startWidth + (moveEvent.clientX - startX));
      setColumnWidths(prev => ({ ...prev, [header]: newWidth }));
    };
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  // Derive headers: use prop if provided, else derive from test case keys, fallback to defaults
  const TC_HEADERS = useMemo(() => {
    if (headersProp && headersProp.length > 0) return headersProp;
    if (testCases.length > 0) {
      const keySet = new Set();
      testCases.forEach(tc => Object.keys(tc).forEach(k => keySet.add(k)));
      return Array.from(keySet).filter(k => !k.startsWith('_'));
    }
    return DEFAULT_TC_HEADERS;
  }, [headersProp, testCases]);

  // Visible headers (all minus hidden)
  const visibleHeaders = useMemo(() =>
    TC_HEADERS.filter(h => !hiddenColumns.has(h)),
    [TC_HEADERS, hiddenColumns]
  );

  const toggleColumn = (header) => {
    setHiddenColumns(prev => {
      const next = new Set(prev);
      next.has(header) ? next.delete(header) : next.add(header);
      return next;
    });
  };

  const handleDoubleClick = (rowIndex, column) => {
    setEditingCell({ rowIndex, column });
    setEditValue(testCases[rowIndex][column] || '');
  };

  const handleEditSave = () => {
    if (editingCell) {
      const updated = [...testCases];
      updated[editingCell.rowIndex] = { ...updated[editingCell.rowIndex], [editingCell.column]: editValue };
      onUpdate(updated);
      setEditingCell(null);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleEditSave();
    if (e.key === 'Escape') setEditingCell(null);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRows(new Set());
      setSelectAll(false);
    } else {
      setSelectedRows(new Set(testCases.map((_, i) => i)));
      setSelectAll(true);
    }
  };

  const handleRowToggle = (index) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      next.has(index) ? next.delete(index) : next.add(index);
      setSelectAll(next.size === testCases.length);
      return next;
    });
  };

  const handleDeleteSelected = () => {
    if (selectedRows.size === 0) return;
    const updated = testCases.filter((_, i) => !selectedRows.has(i));
    onUpdate(updated);
    setSelectedRows(new Set());
    setSelectAll(false);
  };

  const handleAppendCsv = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        const newRows = result.data.map(row => {
          const mapped = {};
          TC_HEADERS.forEach(h => {
            // Try exact match first, then case-insensitive
            const key = Object.keys(row).find(k => k === h) ||
                        Object.keys(row).find(k => k.toLowerCase() === h.toLowerCase()) || '';
            mapped[h] = key ? (row[key] || '') : '';
          });
          return mapped;
        });
        onUpdate([...testCases, ...newRows]);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    });
  };

  const handleAddRow = () => {
    const emptyRow = {};
    TC_HEADERS.forEach(h => { emptyRow[h] = ''; });
    onUpdate([...testCases, emptyRow]);
  };

  return (
    <div className="csv-table-container">
      <div className="table-toolbar" style={{ flexWrap: 'wrap' }}>
        <span className="row-count">{testCases.length} test case{testCases.length !== 1 ? 's' : ''}</span>
        {selectedRows.size > 0 && (
          <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
            {selectedRows.size} selected
          </span>
        )}
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative' }} ref={columnPickerRef}>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setShowColumnPicker(!showColumnPicker)}
              title="Show/hide columns"
            >
              👁 Columns {hiddenColumns.size > 0 && `(${TC_HEADERS.length - hiddenColumns.size}/${TC_HEADERS.length})`}
            </button>
            {showColumnPicker && (
              <div style={{
                position: 'absolute', top: '100%', right: 0, zIndex: 100,
                background: 'white', border: '1px solid var(--border, #ddd)', borderRadius: 8,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)', padding: '8px 0', minWidth: 200, maxHeight: 300, overflowY: 'auto'
              }}>
                <div style={{ padding: '4px 12px 8px', fontSize: '0.75rem', color: '#888', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Toggle columns</span>
                  <span style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => setHiddenColumns(new Set())}
                      style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: '0.75rem' }}
                    >All</button>
                    <button
                      onClick={() => setHiddenColumns(new Set(TC_HEADERS))}
                      style={{ background: 'none', border: 'none', color: 'var(--danger, #d32f2f)', cursor: 'pointer', fontSize: '0.75rem' }}
                    >None</button>
                  </span>
                </div>
                {TC_HEADERS.map(h => (
                  <label key={h} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 12px', cursor: 'pointer', fontSize: '0.82rem' }}>
                    <input
                      type="checkbox"
                      checked={!hiddenColumns.has(h)}
                      onChange={() => toggleColumn(h)}
                      style={{ cursor: 'pointer' }}
                    />
                    {h}
                  </label>
                ))}
              </div>
            )}
          </div>
          <button className="btn btn-secondary btn-sm" onClick={handleAddRow}>+ Add Row</button>
          <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer', margin: 0 }}>
            📎 Append CSV
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleAppendCsv}
              style={{ display: 'none' }}
            />
          </label>
          {selectedRows.size > 0 && (
            <button className="btn btn-danger btn-sm" onClick={handleDeleteSelected}>
              🗑 Delete Selected ({selectedRows.size})
            </button>
          )}
        </span>
      </div>

      <div className="table-scroll" style={{ maxHeight: '50vh' }}>
        <table className="csv-table" role="table">
          <thead>
            <tr>
              <th style={{ width: 40, textAlign: 'center' }}>
                <input type="checkbox" checked={selectAll} onChange={handleSelectAll} aria-label="Select all rows" />
              </th>
              <th className="row-num-header">#</th>
              {visibleHeaders.map(h => (
                <th
                  key={h}
                  className="sortable-header"
                  style={columnWidths[h] ? { width: columnWidths[h], minWidth: columnWidths[h], maxWidth: columnWidths[h] } : undefined}
                >
                  {h}
                  <span
                    className="col-resize-handle"
                    onMouseDown={(e) => handleResizeStart(e, h)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {testCases.map((tc, rowIndex) => (
              <tr key={rowIndex} className={`${rowIndex % 2 === 0 ? 'row-even' : 'row-odd'} ${selectedRows.has(rowIndex) ? 'export-row-selected' : ''}`}>
                <td style={{ textAlign: 'center' }}>
                  <input type="checkbox" checked={selectedRows.has(rowIndex)} onChange={() => handleRowToggle(rowIndex)} aria-label={`Select row ${rowIndex + 1}`} />
                </td>
                <td className="row-num">{rowIndex + 1}</td>
                {visibleHeaders.map(header => {
                  const isEditing = editingCell?.rowIndex === rowIndex && editingCell?.column === header;
                  return (
                    <td
                      key={header}
                      className={`csv-cell editable ${isEditing ? 'editing' : ''}`}
                      onDoubleClick={() => handleDoubleClick(rowIndex, header)}
                    >
                      {isEditing ? (
                        <textarea
                          value={editValue}
                          onChange={e => setEditValue(e.target.value)}
                          onBlur={handleEditSave}
                          onKeyDown={handleKeyDown}
                          className="cell-edit-input"
                          autoFocus
                          rows={3}
                          aria-label={`Edit ${header} for row ${rowIndex + 1}`}
                        />
                      ) : (
                        <span className="cell-value">{tc[header] || ''}</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {testCases.length === 0 && (
        <div className="empty-table">No test cases yet. Generate from a user story or upload a CSV.</div>
      )}

      <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', padding: '8px 16px', margin: 0 }}>
        Double-click a cell to edit · Use checkboxes to select rows for deletion
      </p>
    </div>
  );
}

export default TestCaseTable;
